import { UntypedFormControl } from '@angular/forms';
import { NumberUtils } from '@modeso/aumico-lib-core-fe';
import { AccountType, IAccount, IReclassesAccounts } from '@modeso/types__aumico-lib-annualreport-be';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { IAdjustmentAndReclassesControlAddition } from '../../../interfaces/adjustments-and-reclasses/IAdjustmentAndReclassesControlAddition.interface';
import { AccountMappingEngine } from '../../../mappers/account-mapping-engin';
import { AdjustmentAndReclassesUIHelper } from './adjustmentAndReclassesUI.helper';
const debounceAfter = 200;
export class AdjustmentAndReclassesController {
    constructor(
        private getEngine: () => AccountMappingEngine,
        private reassignDisplayItems: () => void,
        private getDestroy$: () => Observable<unknown>,
        private getUIHelper: () => AdjustmentAndReclassesUIHelper,
    ) {}

    getAdditionControls(account: IReclassesAccounts | IAccount): IAdjustmentAndReclassesControlAddition {
        // REV-20899: This form is the Hidden Reserves (Adjustment) editor;
        // the reclass controls are still emitted for interface compatibility.
        //
        // Whether reclasses are editable from this tab is gated by the
        // UI helper's `readonlyReclasses` flag, which the consuming page
        // derives from `isReclassNewWorld(ar)`. Old-world ARs (legacy
        // reclasses, not v1) leave the flag false so reclass controls stay
        // enabled and users keep editing legacy reclasses here. New-world
        // ARs (reclassificationsVersion === 1, or no legacy reclasses) set
        // the flag true so the controls are disabled; users must use the new
        // transactional reclasses UI in grouping & masking instead.
        const reclassesDisabled = this.getUIHelper().readonlyReclasses === true;
        const rowFormCntrols: IAdjustmentAndReclassesControlAddition = {
            closingAdjustmentControl: new UntypedFormControl(account.closingAdjustment),
            openingAdjustmentControl: new UntypedFormControl(account.openingAdjustment),
            closingReClassControl: new UntypedFormControl({
                value: account.closingReClass,
                disabled: reclassesDisabled,
            }),
            openingReClassControl: new UntypedFormControl({
                value: account.openingReClass,
                disabled: reclassesDisabled,
            }),
            nameControl: account.type === AccountType.RECLASSES ? new UntypedFormControl(account.name) : undefined,
        };

        return rowFormCntrols;
    }

    public detectAccountRowFormValueChanges(
        accountRowFormControls: IAdjustmentAndReclassesControlAddition,
        account: IReclassesAccounts | IAccount,
    ): void {
        accountRowFormControls.closingAdjustmentControl.valueChanges
            .pipe(distinctUntilChanged(), takeUntil(this.getDestroy$()), debounceTime(debounceAfter))
            .subscribe((value) => {
                let closingAdjustment: number | undefined;
                if (NumberUtils.isFinite(value)) {
                    closingAdjustment = NumberUtils.roundNumber(value);
                }
                account.closingAdjustment = closingAdjustment;
                this.calculateAfterChange(account);
            });

        accountRowFormControls.openingAdjustmentControl.valueChanges
            .pipe(distinctUntilChanged(), takeUntil(this.getDestroy$()), debounceTime(debounceAfter))
            .subscribe((value) => {
                let openingAdjustment: number | undefined;
                if (NumberUtils.isFinite(value)) {
                    openingAdjustment = NumberUtils.roundNumber(value);
                }
                account.openingAdjustment = openingAdjustment;
                this.calculateAfterChange(account);
            });
        accountRowFormControls.closingReClassControl.valueChanges
            .pipe(distinctUntilChanged(), takeUntil(this.getDestroy$()), debounceTime(debounceAfter))
            .subscribe((value) => {
                let closingReClass: number | undefined;
                if (NumberUtils.isFinite(value)) {
                    closingReClass = NumberUtils.roundNumber(value);
                }
                account.closingReClass = closingReClass;
                this.calculateAfterChange(account);
            });
        accountRowFormControls.openingReClassControl.valueChanges
            .pipe(distinctUntilChanged(), takeUntil(this.getDestroy$()), debounceTime(debounceAfter))
            .subscribe((value) => {
                let openingReClass: number | undefined;
                if (NumberUtils.isFinite(value)) {
                    openingReClass = NumberUtils.roundNumber(value);
                }
                account.openingReClass = openingReClass;
                this.calculateAfterChange(account);
            });
    }

    public detectReclassesAccountRowFormValueChange(
        reClassesAccountRowFormControls: IAdjustmentAndReclassesControlAddition,
        account: IReclassesAccounts | IAccount,
    ) {
        this.detectAccountRowFormValueChanges(reClassesAccountRowFormControls, account);
        reClassesAccountRowFormControls.nameControl.valueChanges
            .pipe(distinctUntilChanged(), takeUntil(this.getDestroy$()), debounceTime(debounceAfter))
            .subscribe((value) => {
                account.name = value;
            });
    }

    private calculateAfterChange(account: IReclassesAccounts | IAccount) {
        if (account.type !== AccountType.RECLASSES) {
            this.getEngine().updateAccount(account);
        }
        // calculate if account or adjustment change
        this.getEngine().calculateAll(true);
        this.reassignDisplayItems();
    }
}
